import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Box, PlayIcon } from "@hightouch/htui";
import { Player, Thumbnail, type PlayerRef } from "@remotion/player";
import {
  AssetResolverContext,
  CompositionRoot,
  isCustomComponentLayer,
  VideoStillLayer,
  withoutCustomComponentLayers,
  withOnlyLayersAtFrame,
} from "@hightouch/ad-studio-renderer";
import {
  AssetResolverProvider,
  resolveComponentAssetRef,
} from "src/pages/ad-studio/composition/asset-resolver";
import {
  primeComponentAssetUrls,
  resolveComponentAssets,
} from "src/pages/ad-studio/presigned-urls";
import { SandboxedCompositionPlayer } from "src/pages/ad-studio/composition/sandboxed-composition-player";
import { resolveProxyPreferredFilename } from "src/pages/ad-studio/video-proxy";
import { useRegionConfig } from "src/utils/use-region-config";
import { AdCssIsolation } from "src/pages/ad-studio/components/ad-css-isolation";
import type { VideoComposition } from "@hightouch/ad-studio-renderer";

type VideoAssetThumbnailAsset = {
  video_composition?: VideoComposition | null;
  component_assets?: Record<string, string> | null;
  component_asset_urls?: { urls?: Record<string, string> | null } | null;
};

// A still needs one decoded frame, not a player: <Video> in a <Thumbnail>
// buffers ahead and pulls most of the clip. VideoStillLayer honors the same
// trim and crop, so the frame still matches playback.
const THUMBNAIL_VIDEO_OVERRIDE = { "@ad-studio/video": VideoStillLayer };

export default function VideoAssetThumbnail({
  asset,
  iconSize,
  isHovering,
  showPlayIcon = true,
  playOnHover = true,
  imageWidth,
}: {
  asset: VideoAssetThumbnailAsset;
  iconSize?: string;
  /**
   * Lets a parent drive playback when this component's own onMouseEnter can't
   * fire — e.g. the ad card stacks a full-cover click overlay on top, so the
   * cursor never lands on this box. When provided, playback follows it OR the
   * internal hover state. Omitted = self-managed hover (default).
   */
  isHovering?: boolean;
  /** Disable the centered play badge on small tiles where it would cover most
   *  of the content (e.g. sidebar rows, chat pills). */
  showPlayIcon?: boolean;
  /** When false, the thumbnail is purely static — hovering never mounts a
   *  playing Player. Use for tiny incidental tiles (sidebar rows, search
   *  collages, chat pills) where playback would be noise. */
  playOnHover?: boolean;
  /** Loads the still's images through the resizer at this width; hover playback keeps the originals. */
  imageWidth?: number;
}) {
  const [hovering, setHovering] = useState(false);
  const isPlaying = playOnHover && (hovering || Boolean(isHovering));

  // Render phase, not an effect: child effects run first, so a parent effect
  // would fire after the hover player had already started its requests.
  primeComponentAssetUrls({
    componentAssets: asset?.component_assets,
    urls: asset?.component_asset_urls?.urls,
  });

  if (!asset?.video_composition) {
    return null;
  }

  // Custom-component layers carry agent-generated JS, which the inline
  // Thumbnail/Player must strip for security. Compositions that use them get
  // the sandboxed-iframe player instead (heavier, so only when needed) —
  // otherwise their frame 0 can render blank.
  const hasCustomLayers = asset.video_composition.layers.some(
    isCustomComponentLayer,
  );

  return (
    <Box
      width="100%"
      height="100%"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      position="relative"
    >
      {hasCustomLayers ? (
        <SandboxedVideoThumbnailInner
          composition={asset.video_composition}
          componentAssets={asset.component_assets}
          hovering={isPlaying}
        />
      ) : (
        <VideoThumbnailInner
          composition={asset.video_composition}
          componentAssets={asset.component_assets}
          hovering={isPlaying}
          imageWidth={imageWidth}
        />
      )}
      {showPlayIcon && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          bg="blackAlpha.60"
          borderRadius="full"
          p={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          opacity={isPlaying ? 0 : 1}
          transition="opacity 0.2s"
          pointerEvents="none"
          fontSize={iconSize ?? "4xl"}
        >
          <PlayIcon color="white" />
        </Box>
      )}
    </Box>
  );
}

/** Cover-fit: size the composition to fill the container, cropping overflow. */
function useCoverSize(compositionWidth: number, compositionHeight: number) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [coverSize, setCoverSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      if (width === 0 || height === 0) return;
      const scale = Math.max(
        width / compositionWidth,
        height / compositionHeight,
      );
      setCoverSize({
        width: Math.round(compositionWidth * scale),
        height: Math.round(compositionHeight * scale),
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [compositionWidth, compositionHeight]);

  return { containerRef, coverSize };
}

/**
 * Thumbnail for compositions with agent-generated custom-component layers:
 * a paused SandboxedCompositionPlayer (opaque-origin iframe) showing frame 0,
 * driven to play/pause by the hover state. The inline path below can't render
 * these layers — it strips them for security.
 */
function SandboxedVideoThumbnailInner({
  composition,
  componentAssets,
  hovering,
}: {
  composition: VideoComposition;
  componentAssets: Record<string, string> | null | undefined;
  hovering: boolean;
}) {
  const { containerRef, coverSize } = useCoverSize(
    composition.width,
    composition.height,
  );
  const playerRef = useRef<PlayerRef>(null);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    if (hovering) {
      player.play();
    } else {
      player.pause();
      player.seekTo(0);
    }
  }, [hovering]);

  return (
    <Box ref={containerRef} position="absolute" inset={0} overflow="hidden">
      {coverSize && (
        <SandboxedCompositionPlayer
          composition={composition}
          componentAssets={componentAssets}
          playerRef={playerRef}
          controls={false}
          clickToPlay={false}
          initiallyMuted
          loop
          style={{
            width: coverSize.width,
            height: coverSize.height,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            // Thumbnails aren't interactive — let clicks/hover fall through to
            // the card (its link overlay and hover tracking own the pointer).
            pointerEvents: "none",
          }}
        />
      )}
    </Box>
  );
}

function VideoThumbnailInner({
  composition: rawComposition,
  componentAssets,
  hovering,
  imageWidth,
}: {
  composition: VideoComposition;
  componentAssets: Record<string, string> | null | undefined;
  hovering: boolean;
  imageWidth?: number;
}) {
  // Thumbnails render inline (no sandbox iframe), so we strip out any
  // agent-generated custom-component layers — the rest of the composition
  // (built-in layers like html-overlay, video, captions) still renders.
  const composition = useMemo(
    () => withoutCustomComponentLayers(rawComposition),
    [rawComposition],
  );
  const { containerRef, coverSize } = useCoverSize(
    composition.width,
    composition.height,
  );

  const durationInFrames = useMemo(
    () => Math.max(1, ...composition.layers.map((l) => l.from + l.duration)),
    [composition.layers],
  );

  // The still only shows frame 0, but layers premount for the whole
  // composition — so drop the ones that have not started. `durationInFrames`
  // above stays derived from the full composition, since the hover Player
  // still plays it end to end.
  const stillComposition = useMemo(
    () => withOnlyLayersAtFrame(composition, 0),
    [composition],
  );

  // A fresh object here busts the Thumbnail's composition-context memo, which
  // re-runs the video layer's effects — an extraction per re-render.
  const inputProps = useMemo(
    () => ({
      composition: stillComposition,
      customRegistry: THUMBNAIL_VIDEO_OVERRIDE,
    }),
    [stillComposition],
  );

  if (!coverSize) {
    return <Box ref={containerRef} position="absolute" inset={0} />;
  }

  const sharedProps = {
    component: CompositionRoot,
    durationInFrames,
    compositionWidth: composition.width,
    compositionHeight: composition.height,
    fps: composition.fps,
  } as const;

  const coverStyle = {
    width: coverSize.width,
    height: coverSize.height,
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  };

  return (
    <Box ref={containerRef} position="absolute" inset={0}>
      <AdCssIsolation />
      {/* The hover player brings its own resolver, so the width reaches only the still. */}
      <AssetResolverProvider
        componentAssets={componentAssets}
        preferProxy
        imageWidth={imageWidth}
      >
        <Thumbnail
          {...sharedProps}
          inputProps={inputProps}
          frameToDisplay={0}
          style={coverStyle}
        />
        {hovering && (
          <PresignedHoverPlayer
            composition={composition}
            componentAssets={componentAssets}
            durationInFrames={durationInFrames}
            style={coverStyle}
          />
        )}
      </AssetResolverProvider>
    </Box>
  );
}

/**
 * Hover playback, mounted once the clips resolve to presigned URLs — until
 * then the frame-0 <Thumbnail> underneath stays visible. The presigning is
 * what the Player needs: the cookie-authed /agents/files URLs 302 to storage,
 * and the ranged CORS reads its media layers issue can't follow that redirect.
 *
 * The bytes themselves are not pre-downloaded. Media layers range-read the
 * clip as they play, so pulling whole files up front only delayed playback,
 * and a prefetch resolves the layer's src to a blob URL that the media
 * pipeline fetches rather than assigns to a <video> element, which the app's
 * CSP blocks (`connect-src` has no `blob:`).
 */
function PresignedHoverPlayer({
  composition,
  componentAssets,
  durationInFrames,
  style,
}: {
  composition: VideoComposition;
  componentAssets: Record<string, string> | null | undefined;
  durationInFrames: number;
  style: CSSProperties;
}) {
  const { apiEndpoint } = useRegionConfig();
  const [resolver, setResolver] = useState<((ref: string) => string) | null>(
    null,
  );

  useEffect(() => {
    let canceled = false;
    void (async () => {
      const { byFilename } = await resolveComponentAssets({
        componentAssets,
        apiEndpoint,
        targetRenderer: "hightouch-webapp",
      });
      // Prefer the proxy transcode, as the <Thumbnail> underneath already
      // does: the unsuffixed clip is the full-resolution render, so without
      // this a hover streams the master instead of the small proxy.
      const resolve = (ref: string) => {
        if (!ref.startsWith("ad:")) return ref;
        const filename = resolveProxyPreferredFilename(
          ref.slice(3),
          componentAssets,
        );
        // Filenames are agent- and user-supplied, so a bare index would
        // resolve to an Object.prototype member for names like `constructor`.
        return Object.hasOwn(byFilename, filename)
          ? (byFilename[filename] ?? ref)
          : ref;
      };
      if (!canceled) setResolver(() => resolve);
    })().catch((err: Error) => {
      // Presigning failed — mount anyway with the cookie-authed resolver and
      // let the redirect play out.
      console.warn("[ad-studio] hover-play presigning failed", err);
      if (!canceled) {
        setResolver(
          () => (ref: string) =>
            resolveComponentAssetRef(ref, componentAssets, apiEndpoint, {
              preferProxy: true,
            }),
        );
      }
    });
    return () => {
      canceled = true;
    };
  }, [componentAssets, apiEndpoint]);

  if (!resolver) return null;

  return (
    <AssetResolverContext.Provider value={resolver}>
      <Player
        component={CompositionRoot}
        inputProps={{ composition, customRegistry: {} }}
        durationInFrames={durationInFrames}
        compositionWidth={composition.width}
        compositionHeight={composition.height}
        fps={composition.fps}
        initiallyMuted
        controls={false}
        autoPlay
        loop
        style={style}
      />
    </AssetResolverContext.Provider>
  );
}
